import type {BlockName} from 'quickstart'

/**
 * One entry in a section. Each kind is independently skippable: an admin that
 * does not understand a kind drops that item and renders the rest.
 *
 * - `globalAdmin` — mount this block's global admin here.
 * - `paramGroup` — the admin's own named group of design parameters goes here.
 *   We name the group; which parameters are in it is the admin's to say.
 * - `param` — this one design parameter, if the site defines one. Sites differ,
 *   so an absent parameter is the ordinary case rather than an error.
 * - `heading` — labels a subgroup, introducing the items that follow it up to
 *   the next heading or the end of the section.
 */
export type DesignTabItem =
  | {globalAdmin: BlockName}
  | {paramGroup: string}
  | {param: string}
  | {heading: string}

/**
 * A group of items. `id` is an identifier rather than a slug of `title`: the
 * admin routes and deep-links on it, so revising the copy must not break saved
 * links. `title` is optional because a section may present without a heading.
 */
export interface DesignTabSection {
  id: string
  title?: string
  items: DesignTabItem[]
}

export interface DesignTab {
  sections: DesignTabSection[]
}

/**
 * How we organize the admin's design tabs. This describes structure only --
 * identity, label, order, contents -- and deliberately names no widget, class
 * or element, so the admin can draw a section as a heading, a fold or a
 * navigation pane without a release here.
 *
 * Which tabs exist is the admin's; it names one when it asks.
 */
const designTabs: Record<string, DesignTab> = {
  features: {
    sections: [
      {
        id: 'authentication',
        title: 'Authentication',
        items: [{globalAdmin: 'signin'}],
      },
      {
        id: 'bookshelf',
        title: 'Bookshelf',
        items: [{globalAdmin: 'bookshelf'}],
      },
      // Design parameters are a server concept, so we place the group without
      // enumerating it.
      {id: 'reader', title: 'Reader', items: [{paramGroup: 'reader'}]},
      {
        id: 'redemption-codes',
        title: 'Redemption codes',
        items: [{globalAdmin: 'redemption'}],
      },
      {id: 'search', title: 'Search', items: [{globalAdmin: 'search'}]},
    ],
  },
  nav: {
    sections: [
      {id: 'nav', title: 'Main Nav', items: [{globalAdmin: 'nav'}]},
      {id: 'footer', title: 'Footer', items: [{globalAdmin: 'footer'}]},
    ],
  },
  theme: {
    sections: [{id: 'theme', items: [{globalAdmin: 'theme'}]}],
  },
}

/**
 * Describe the organization of a design tab the admin names.
 *
 * Reads structure only: no DOM, no admin lookups, no rendering.
 *
 * @param viewId - the admin's identifier for the tab it is rendering
 * @returns the tab's sections, or undefined for a tab we don't organize
 */
export const getDesignTab = (viewId: string): DesignTab | undefined =>
  // hasOwn rather than a bare lookup: viewId comes from the admin, and
  // 'constructor' must be as unknown to us as any other tab we don't organize.
  Object.hasOwn(designTabs, viewId) ? designTabs[viewId] : undefined
